import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface ServerErrorProps {
  statusCode?: number
  message?: string
}

/**
 * Server error page for 502, 503, 504 and other 5xx errors.
 * Shows user-friendly message with retry and navigation options.
 */
export function ServerError({ statusCode: propStatusCode, message: propMessage }: ServerErrorProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isRetrying, setIsRetrying] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  const statusCode = propStatusCode || Number(searchParams.get('code')) || 502
  const message = propMessage || searchParams.get('message') || undefined

  const errorConfig: Record<number, { title: string; description: string }> = {
    502: {
      title: 'Bad Gateway',
      description: 'The server received an invalid response. This is usually temporary.',
    },
    503: {
      title: 'Service Unavailable',
      description: 'The server is currently unavailable. It may be undergoing maintenance.',
    },
    504: {
      title: 'Gateway Timeout',
      description: 'The server took too long to respond. Please try again.',
    },
    500: {
      title: 'Internal Server Error',
      description: 'Something went wrong on our end. We\'re working to fix it.',
    },
  }

  const error = errorConfig[statusCode] || errorConfig[500]

  const handleRetry = useCallback(async () => {
    setIsRetrying(true)
    setRetryCount((c) => c + 1)

    // Wait a moment then try to go back
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Try to go back in history, or go to dashboard if no history
    if (window.history.length > 2) {
      navigate(-1)
    } else {
      navigate('/dashboard')
    }
    setIsRetrying(false)
  }, [navigate])

  const handleGoHome = () => {
    navigate('/dashboard')
  }

  // Auto-retry after 10 seconds for 503 errors (maintenance)
  useEffect(() => {
    if (statusCode === 503 && retryCount < 3) {
      const timer = setTimeout(handleRetry, 10000)
      return () => clearTimeout(timer)
    }
  }, [statusCode, retryCount, handleRetry])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
          <svg className="h-10 w-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
        </div>
        <div className="mb-2 text-5xl font-bold text-gray-900">{statusCode}</div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">{error.title}</h1>
        <p className="mb-6 text-gray-600">
          {message || error.description}
        </p>
        {statusCode === 503 && retryCount < 3 && (
          <p className="mb-4 text-sm text-gray-500">
            Auto-retrying in a few seconds...
          </p>
        )}
        <div className="flex justify-center gap-4">
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Try again'}
          </button>
          <button
            onClick={handleGoHome}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Go to Dashboard
          </button>
        </div>
        {retryCount > 0 && (
          <p className="mt-4 text-xs text-gray-400">
            Retry attempts: {retryCount}
          </p>
        )}
      </div>
    </div>
  )
}
